import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import MotionMarquee from './components/MotionMarquee';
import Button from './components/Button';
import Card from './components/Card';
import Input from './components/Input';
import NoiseTexture from './components/NoiseTexture';
import PricingPage from './components/PricingPage';
import ClientsPage from './components/ClientsPage';
import ContactPage from './components/ContactPage';
import WorkPage from './components/WorkPage';
import WebDesignPage from './components/WebDesignPage';
import WebDesignChicagoPage from './components/WebDesignChicagoPage';
import SocialMediaPage from './components/SocialMediaPage';
import LogosPage from './components/LogosPage';
import AutomationPage from './components/AutomationPage';
import MicroservicesPage from './components/MicroservicesPage';
import AboutPage from './components/AboutPage';
import PayPage from './components/PayPage';
import SuccessPage from './components/SuccessPage';
import windLogo from './wind-logo.png';
import windLogoWhite from './logoWhite.png';
import './App.css';
import { BRAND, BRAND_SHORT, brandify } from './brandConfig';

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  // Stripe sweep animation offset (% units, shifts clip-path x coords)
  const [stripeOffset, setStripeOffset] = useState(-50);
  // Tracks whether the hero title scale-in has already played
  const [heroAnimPlayed, setHeroAnimPlayed] = useState(false);

  // Contact form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formStatus, setFormStatus] = useState('idle'); // idle | sending | success | error
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    setFormError('');
    try {
      const serviceInterest = window.history.state?.serviceInterest || '';
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formName, email: formEmail, message: formMessage, service: serviceInterest }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFormError(data.error || 'Something went wrong.');
        setFormStatus('error');
      } else {
        setFormStatus('success');
        setFormName('');
        setFormEmail('');
        setFormMessage('');
      }
    } catch (err) {
      setFormError('Could not reach the server. Make sure it is running.');
      setFormStatus('error');
    }
  };

  // Listen for history navigation (e.g. browser back/forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path, state = {}, preserveScroll = false) => {
    window.history.pushState(state, '', path);
    setCurrentPath(path);
    if (!preserveScroll) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  };



  // Custom scroll tracking for React 19 compatibility
  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollHeight > 0 ? window.scrollY / scrollHeight : 0;
      setScrollProgress(progress);
    };

    updateScrollProgress();
    window.addEventListener('scroll', updateScrollProgress, { passive: true });
    window.addEventListener('resize', updateScrollProgress);

    return () => {
      window.removeEventListener('scroll', updateScrollProgress);
      window.removeEventListener('resize', updateScrollProgress);
    };
  }, []);

  // One-shot stripe sweep animation on mount
  useEffect(() => {
    const DURATION = 1000; // ms
    const START_OFFSET = -50; // off-screen left
    const END_OFFSET   = 97;  // off-screen right
    let startTime = null;
    let rafId;

    // Smooth elastic-in-out: cubic ease for the sweep, with a gentle
    // overshoot on exit so it feels springy without bouncing backwards.
    const easing = (t) => {
      if (t <= 0) return 0;
      if (t >= 1) return 1;
      // Ease-in-out cubic — smooth, single-direction, no backwards movement
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const t = Math.min((timestamp - startTime) / DURATION, 1);
      const eased = easing(t);
      setStripeOffset(START_OFFSET + (END_OFFSET - START_OFFSET) * eased);
      if (t < 1) rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Calculate hero transform values based on scroll progress
  const heroScale = React.useMemo(() => {
    if (scrollProgress <= 0.2) {
      return 1 + (scrollProgress / 0.2) * 0.2; // 1 to 1.2
    }
    return 1.2;
  }, [scrollProgress]);

  const heroOpacity = React.useMemo(() => {
    if (scrollProgress <= 0.2) {
      return 1 - (scrollProgress / 0.2); // 1 to 0
    }
    return 0;
  }, [scrollProgress]);

  return (
    <div className="App min-h-screen bg-background text-foreground">
      <NoiseTexture />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-background border-b-2 border-border">
        <div className="max-w-[95vw] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div 
            onClick={() => navigate('/')} 
            className="text-2xl md:text-4xl font-bold uppercase tracking-tighter flex items-start cursor-pointer select-none" 
            style={{ fontFamily: "'Poppins Black', sans-serif" }}
          >
            <img src={windLogo} alt="Wind Logo" className="w-10 h-10 mr-2 flex-shrink-0" />
            <span className="hidden md:inline" style={{ marginTop: '5px', marginLeft: '-2px' }}>WIND</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="/work" onClick={(e) => { e.preventDefault(); navigate('/work'); }} className={`text-sm uppercase tracking-wide hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background ${currentPath === '/work' ? 'text-accent font-bold' : ''}`}>Work</a>
            <div className="relative group py-2">
              <a 
                href="/work" 
                onClick={(e) => { e.preventDefault(); navigate('/work'); }} 
                className="text-sm uppercase tracking-wide hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background flex items-center gap-1"
              >
                Services
                <svg className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"></path>
                </svg>
              </a>
              {/* Dropdown Menu */}
              <div className="absolute top-full left-0 hidden group-hover:flex flex-col bg-background border-2 border-border p-2 w-48 shadow-[6px_6px_0px_#0101ff] z-50">
                <a href="/web-design" onClick={(e) => { e.preventDefault(); navigate('/web-design'); }} className={`text-xs uppercase tracking-wide px-3 py-2.5 hover:bg-accent hover:text-accent-foreground transition-colors font-bold ${currentPath === '/web-design' ? 'text-accent' : 'text-muted-foreground hover:text-foreground'}`}>Web Design</a>
                <a href="/social-media" onClick={(e) => { e.preventDefault(); navigate('/social-media'); }} className={`text-xs uppercase tracking-wide px-3 py-2.5 hover:bg-accent hover:text-accent-foreground transition-colors font-bold ${currentPath === '/social-media' ? 'text-accent' : 'text-muted-foreground hover:text-foreground'}`}>Social Media</a>
                <a href="/logos" onClick={(e) => { e.preventDefault(); navigate('/logos'); }} className={`text-xs uppercase tracking-wide px-3 py-2.5 hover:bg-accent hover:text-accent-foreground transition-colors font-bold ${currentPath === '/logos' ? 'text-accent' : 'text-muted-foreground hover:text-foreground'}`}>Logos</a>
                <a href="/automation" onClick={(e) => { e.preventDefault(); navigate('/automation'); }} className={`text-xs uppercase tracking-wide px-3 py-2.5 hover:bg-accent hover:text-accent-foreground transition-colors font-bold ${currentPath === '/automation' ? 'text-accent' : 'text-muted-foreground hover:text-foreground'}`}>Automation</a>
                <a href="/microservices" onClick={(e) => { e.preventDefault(); navigate('/microservices'); }} className={`text-xs uppercase tracking-wide px-3 py-2.5 hover:bg-accent hover:text-accent-foreground transition-colors font-bold ${currentPath === '/microservices' ? 'text-accent' : 'text-muted-foreground hover:text-foreground'}`}>Microservices</a>
              </div>
            </div>
            <a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }} className={`text-sm uppercase tracking-wide hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background ${currentPath === '/pricing' ? 'text-accent font-bold' : ''}`}>Pricing</a>
            <a href="/clients" onClick={(e) => { e.preventDefault(); navigate('/clients'); }} className={`text-sm uppercase tracking-wide hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background ${currentPath === '/clients' ? 'text-accent font-bold' : ''}`}>Clients</a>
            <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }} className={`text-sm uppercase tracking-wide hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background ${currentPath === '/contact' ? 'text-accent font-bold' : ''}`}>Contact</a>
          </div>
          <div className="flex items-center gap-4">
            <Button size="small" className="hidden md:block" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>
              Get Started
            </Button>
            <button
              className="md:hidden w-10 h-10 flex flex-col justify-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span className={`w-full h-0.5 bg-foreground transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`w-full h-0.5 bg-foreground transition-all ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`w-full h-0.5 bg-foreground transition-all ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-background">
            <div className="flex flex-col p-4 gap-4">
              <a href="/work" className={`text-sm uppercase tracking-wide hover:text-accent transition-colors py-2 ${currentPath === '/work' ? 'text-accent font-bold' : ''}`} onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/work'); }}>Work</a>
              <div className="flex flex-col">
                <button 
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="flex items-center justify-between text-sm uppercase tracking-wide hover:text-accent transition-colors py-2 text-left w-full focus:outline-none"
                >
                  <span>Services</span>
                  <svg className={`w-4 h-4 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                {mobileServicesOpen && (
                  <div className="flex flex-col pl-4 border-l-2 border-border gap-2 mt-1 mb-2">
                    <a href="/web-design" className={`text-xs uppercase tracking-wide hover:text-accent transition-colors py-2 ${currentPath === '/web-design' ? 'text-accent font-bold' : ''}`} onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/web-design'); }}>Web Design</a>
                    <a href="/social-media" className={`text-xs uppercase tracking-wide hover:text-accent transition-colors py-2 ${currentPath === '/social-media' ? 'text-accent font-bold' : ''}`} onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/social-media'); }}>Social Media</a>
                    <a href="/logos" className={`text-xs uppercase tracking-wide hover:text-accent transition-colors py-2 ${currentPath === '/logos' ? 'text-accent font-bold' : ''}`} onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/logos'); }}>Logos</a>
                    <a href="/automation" className={`text-xs uppercase tracking-wide hover:text-accent transition-colors py-2 ${currentPath === '/automation' ? 'text-accent font-bold' : ''}`} onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/automation'); }}>Automation</a>
                    <a href="/microservices" className={`text-xs uppercase tracking-wide hover:text-accent transition-colors py-2 ${currentPath === '/microservices' ? 'text-accent font-bold' : ''}`} onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/microservices'); }}>Microservices</a>
                  </div>
                )}
              </div>
              <a href="/pricing" className={`text-sm uppercase tracking-wide hover:text-accent transition-colors py-2 ${currentPath === '/pricing' ? 'text-accent font-bold' : ''}`} onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/pricing'); }}>Pricing</a>
              <a href="/clients" className={`text-sm uppercase tracking-wide hover:text-accent transition-colors py-2 ${currentPath === '/clients' ? 'text-accent font-bold' : ''}`} onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/clients'); }}>Clients</a>
              <a href="/contact" className={`text-sm uppercase tracking-wide hover:text-accent transition-colors py-2 ${currentPath === '/contact' ? 'text-accent font-bold' : ''}`} onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/contact'); }}>Contact</a>
              <Button size="small" className="w-full" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/contact'); }}>
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      {currentPath === '/pricing' ? (
        <PricingPage navigate={navigate} />
      ) : currentPath === '/clients' ? (
        <ClientsPage navigate={navigate} />
      ) : currentPath === '/contact' ? (
        <ContactPage navigate={navigate} />
      ) : currentPath === '/pay' ? (
        <PayPage navigate={navigate} />
      ) : currentPath === '/success' || window.location.search.includes('success=true') ? (
        <SuccessPage navigate={navigate} />
      ) : currentPath === '/work' ? (
        <WorkPage navigate={navigate} />
      ) : currentPath === '/web-design' ? (
        <WebDesignPage navigate={navigate} />
      ) : currentPath === '/web-design-chicago' ? (
        <WebDesignChicagoPage navigate={navigate} />
      ) : currentPath === '/social-media' ? (
        <SocialMediaPage navigate={navigate} />
      ) : currentPath === '/logos' ? (
        <LogosPage navigate={navigate} />
      ) : currentPath === '/automation' ? (
        <AutomationPage navigate={navigate} />
      ) : currentPath === '/microservices' ? (
        <MicroservicesPage navigate={navigate} />
      ) : currentPath === '/about' ? (
        <AboutPage navigate={navigate} />
      ) : (
        <>
          {/* SEO Helmet for Home Page */}
          <Helmet>
            <title>{`${BRAND} | Small Business Web Design & Marketing — Chicago`}</title>
            <meta name="description" content={brandify('Wind Marketing is a Chicago-based digital marketing agency specializing in small business website design, brand strategy, social media, automation, and digital campaigns. Marketing that moves.')} />
            <meta name="keywords" content={brandify('Wind Marketing, small business website designer, web design services for small business, Chicago web design, digital marketing agency, brand strategy, social media marketing')} />
            <link rel="canonical" href="https://www.wind-marketing.com/" />

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://www.wind-marketing.com/" />
            <meta property="og:title" content={`${BRAND} • Marketing That Moves`} />
            <meta property="og:description" content={brandify('Wind Marketing is a full-service digital marketing agency specializing in high-impact websites, brand strategy, social media, automation, and digital campaigns. Marketing that moves.')} />
            <meta property="og:image" content="https://www.wind-marketing.com/social_preview.png" />
            <meta property="og:site_name" content={BRAND} />
            <meta property="og:locale" content="en_US" />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={`${BRAND} • Marketing That Moves`} />
            <meta name="twitter:description" content={brandify('Wind Marketing is a full-service digital marketing agency specializing in high-impact websites, brand strategy, social media, automation, and digital campaigns.')} />
            <meta name="twitter:image" content="https://www.wind-marketing.com/social_preview.png" />

            {/* Icons — force Safari to pick up the correct icon */}
            <link rel="icon" type="image/x-icon" href="/favicon.ico" />
            <link rel="icon" type="image/png" sizes="192x192" href="/logo192.png" />
            <link rel="icon" type="image/png" sizes="512x512" href="/logo512.png" />
            <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />

            {/* Structured Data — JSON-LD: WebSite (controls site name in Google results) */}
            <script type="application/ld+json">{`
              {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "${BRAND}",
                "alternateName": "${BRAND_SHORT}",
                "url": "https://www.wind-marketing.com"
              }
            `}</script>

            {/* Structured Data — JSON-LD: Organization */}
            <script type="application/ld+json">{`
              {
                "@context": "https://schema.org",
                "@type": "ProfessionalService",
                "@id": "https://www.wind-marketing.com/#organization",
                "name": "${BRAND}",
                "alternateName": "${BRAND_SHORT}",
                "url": "https://www.wind-marketing.com",
                "logo": "https://www.wind-marketing.com/logo512.png",
                "description": "${brandify('Wind Marketing is a Chicago-based digital marketing agency delivering small business website design, brand strategy, social media, automation, and measurable digital campaigns.')}",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Chicago",
                  "addressRegion": "IL",
                  "addressCountry": "US"
                },
                "areaServed": [
                  { "@type": "City", "name": "Chicago" },
                  { "@type": "Country", "name": "United States" }
                ],
                "priceRange": "$$",
                "sameAs": [
                  "https://www.linkedin.com/company/windmarketing",
                  "https://www.instagram.com/i.am.justinmoran",
                  "https://www.youtube.com/@justinmoran"
                ],
                "contactPoint": {
                  "@type": "ContactPoint",
                  "contactType": "customer service",
                  "url": "https://www.wind-marketing.com/contact"
                }
              }
            `}</script>

            {/* Structured Data — JSON-LD: FAQ (boosts AI answer engine inclusion) */}
            <script type="application/ld+json">{`
              {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "What is ${BRAND}?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "${brandify('Wind Marketing is a full-service digital marketing agency that delivers high-impact websites, brand strategy, social media marketing, automation systems, and custom microservices for growth-stage businesses. Their tagline is \'Marketing That Moves.\'')}"
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What services does ${BRAND} offer?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "${brandify('Wind Marketing offers five core services: Web Design (custom conversion-focused websites), Social Media Marketing (multi-channel content and community management), Logo and Brand Identity Design, Marketing Automation (workflow and data integration systems), and Custom Microservices Development (backend APIs and payment systems).')}"
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "How do I get started with ${BRAND}?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "${brandify('You can start by visiting wind-marketing.com/contact to submit a project inquiry, or browse wind-marketing.com/pricing to view available service plans. Wind Marketing works with clients globally.')}"
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Where is ${BRAND} based?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "${brandify('Wind Marketing is based in Chicago, Illinois, and operates as a digital-first agency serving small businesses across Chicagoland and nationwide. Contact and project inquiries can be made through wind-marketing.com/contact.')}"
                    }
                  }
                ]
              }
            `}</script>
          </Helmet>

          {/* Hero Section */}
          <section className="relative min-h-screen flex items-start md:items-center justify-center overflow-hidden pt-12 md:pt-24">
        <div
          style={{ 
            transform: `scale(${heroScale})`,
            opacity: heroOpacity
          }}
          className="max-w-[95vw] mx-auto px-4 md:px-8 text-center"
        >
          <h1
            className={`${!heroAnimPlayed ? 'hero-title-animate' : ''} text-hero font-bold uppercase tracking-tighter leading-none mb-4 md:mb-8 pt-[80px] md:pt-0`}
            onAnimationEnd={() => setHeroAnimPlayed(true)}
          >
            <span className='text-[clamp(4rem,13vw,16rem)]'>MARKETING</span>
            <br />
            {/* Dual-layer diagonal split-color effect on "THAT MOVES" */}
            <span
              className="relative inline-block"
              style={{ fontSize: 'clamp(3.8rem,12vw,16rem)', lineHeight: 1 }}
              aria-label="THAT MOVES"
            >
              {/* Base layer: solid blue */}
              <span
                className="text-accent"
                style={{ display: 'block', whiteSpace: 'nowrap' }}
              >
                THAT MOVES
              </span>
              {/* Top layer: white, main diagonal stripe */}
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  color: '#ffffff',
                  clipPath: `polygon(${(20 + stripeOffset).toFixed(2)}% 0%, ${(42 + stripeOffset).toFixed(2)}% 0%, ${(18 + stripeOffset).toFixed(2)}% 100%, ${stripeOffset.toFixed(2)}% 100%)`,
                }}
              >
                THAT MOVES
              </span>
              {/* Secondary diagonal stripe — 1/5.5 width of main, 7% gap to the right */}
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  color: '#ffffff',
                  clipPath: `polygon(${(49 + stripeOffset).toFixed(2)}% 0%, ${(53 + stripeOffset).toFixed(2)}% 0%, ${(28 + stripeOffset).toFixed(2)}% 100%, ${(24 + stripeOffset).toFixed(2)}% 100%)`,
                }}
              >
                THAT MOVES
              </span>
            </span>
          </h1>
          <p className="text-[19.5px] md:text-xl lg:text-2xl text-muted-foreground max-w-2xl mx-auto mt-[30px] md:mt-0 mb-[80px] md:mb-12 leading-tight">
          We design websites and campaigns driving engagement, conversion, and growth for small businesses.
            </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Button size="large" onClick={() => navigate('/contact')}>Start Your Campaign</Button>
            <Button variant="outline" size="large" onClick={() => navigate('/work')}>View Our Work</Button>
          </div>
        </div>
      </section>

      {/* Stats Marquee */}
      <section className="py-12 bg-accent text-accent-foreground border-y-2 border-accent">
        <div className="overflow-hidden">
          <MotionMarquee speed={80}>
            <div className="flex items-center gap-12 mx-8">
              <div className="text-[6rem] md:text-[8rem] font-bold">10+</div>
              <div className="text-xl md:text-2xl uppercase tracking-wide">Campaigns Launched</div>
              <div className="font-bold text-[3.5rem] translate-y-[-10px] md:text-[5rem] md:translate-y-[-14px]">༄</div>
              <div className="text-[6rem] md:text-[8rem] font-bold">17M+</div>
              <div className="text-xl md:text-2xl uppercase tracking-wide">Views Generated</div>
              <div className="font-bold text-[3.5rem] translate-y-[-10px] md:text-[5rem] md:translate-y-[-14px]">༄</div>
              <div className="text-[6rem] md:text-[8rem] font-bold">98%</div>
              <div className="text-xl md:text-2xl uppercase tracking-wide">Client Satisfaction</div>
              <div className="font-bold text-[3.5rem] translate-y-[-10px] md:text-[5rem] md:translate-y-[-14px]">༄</div>
              <div className="text-[6rem] md:text-[8rem] font-bold">7+</div>
              <div className="text-xl md:text-2xl uppercase tracking-wide">Years Experience</div>
              <div className="font-bold text-[3.5rem] translate-y-[-10px] md:text-[5rem] md:translate-y-[-14px]">༄</div>
            </div>
          </MotionMarquee>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 max-w-[95vw] mx-auto px-4 md:px-8">
        <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter mb-20 leading-none">
          WHAT WE DO
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <Card>
            <div className="text-muted text-[8rem] md:text-[12rem] font-bold leading-none mb-4 group-hover:text-accent-foreground transition-colors">
              01
            </div>
            <h3 className="text-2xl md:text-3xl lg:text-6xl font-bold uppercase tracking-tighter mb-4 group-hover:text-accent-foreground transition-colors">
              BRAND STRATEGY
            </h3>
            <p className="text-lg md:text-xl text-muted-foreground group-hover:text-accent-foreground/80 transition-colors">
              We don't just design logos; we architect brand identities that command attention and drive action.
            </p>
          </Card>
          <Card>
            <div className="text-muted text-[8rem] md:text-[12rem] font-bold leading-none mb-4 group-hover:text-accent-foreground transition-colors">
              02
            </div>
            <h3 className="text-2xl md:text-3xl lg:text-6xl font-bold uppercase tracking-tighter mb-4 group-hover:text-accent-foreground transition-colors">
              DIGITAL CAMPAIGNS
            </h3>
            <p className="text-lg md:text-xl text-muted-foreground group-hover:text-accent-foreground/80 transition-colors">
              Multi-channel campaigns that break through the noise and connect with audiences where they live.
            </p>
          </Card>
          <Card>
            <div className="text-muted text-[8rem] md:text-[12rem] font-bold leading-none mb-4 group-hover:text-accent-foreground transition-colors">
              03
            </div>
            <h3 className="text-2xl md:text-3xl lg:text-6xl font-bold uppercase tracking-tighter mb-4 group-hover:text-accent-foreground transition-colors">
              CONTENT CREATION
            </h3>
            <p className="text-lg md:text-xl text-muted-foreground group-hover:text-accent-foreground/80 transition-colors">
              High-impact content that tells your story with the urgency and energy it deserves.
            </p>
          </Card>
        </div>
      </section>

      {/* Features Section with Sticky Scroll */}
      <section id="work" className="py-32 max-w-[95vw] mx-auto px-4 md:px-8">
        <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter mb-20 leading-none">
          WHY IT WORKS
        </h2>
        <div className="space-y-8">
          <div className="sticky top-32">
            <Card>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
                <div className="text-muted text-[6rem] md:text-[8rem] font-bold leading-none flex-shrink-0 group-hover:text-accent-foreground transition-colors">
                  01
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl md:text-4xl lg:text-6xl font-bold uppercase tracking-tighter mb-4 group-hover:text-accent-foreground transition-colors">
                    BOLD VISUALS
                  </h3>
                  <p className="text-lg md:text-xl text-muted-foreground group-hover:text-accent-foreground/80 transition-colors">
                    We reject subtlety. Your brand deserves visuals that stop scrolling and start conversations.
                  </p>
                </div>
              </div>
            </Card>
          </div>
          <div className="sticky top-32">
            <Card>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
                <div className="text-muted text-[6rem] md:text-[8rem] font-bold leading-none flex-shrink-0 group-hover:text-accent-foreground transition-colors">
                  02
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl md:text-4xl lg:text-6xl font-bold uppercase tracking-tighter mb-4 group-hover:text-accent-foreground transition-colors">
                    DATA-DRIVEN
                  </h3>
                  <p className="text-lg md:text-xl text-muted-foreground group-hover:text-accent-foreground/80 transition-colors">
                    Every decision is backed by analytics. We measure what matters and optimize relentlessly.
                  </p>
                </div>
              </div>
            </Card>
          </div>
          <div className="sticky top-32">
            <Card>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
                <div className="text-muted text-[6rem] md:text-[8rem] font-bold leading-none flex-shrink-0 group-hover:text-accent-foreground transition-colors">
                  03
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl md:text-4xl lg:text-6xl font-bold uppercase tracking-tighter mb-4 group-hover:text-accent-foreground transition-colors">
                    FAST EXECUTION
                  </h3>
                  <p className="text-lg md:text-xl text-muted-foreground group-hover:text-accent-foreground/80 transition-colors">
                    Speed is a competitive advantage. We move fast, ship faster, and iterate in real-time.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Marquee */}
      <section className="py-32 bg-muted">
        <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter mb-20 text-center max-w-[95vw] mx-auto px-4 md:px-8">
          WHAT CLIENTS SAY
        </h2>
        <div className="overflow-hidden">
          <MotionMarquee speed={40}>
            <div className="flex gap-8 mx-4">
              <Card className="min-w-[400px] md:min-w-[500px]">
                <p className="text-2xl md:text-3xl font-bold uppercase tracking-tighter mb-6 group-hover:text-accent-foreground transition-colors">
                  "WIND TRANSFORMED OUR BRAND FROM INVISIBLE TO UNAVOIDABLE."
                </p>
                <p className="text-lg text-muted-foreground group-hover:text-accent-foreground/80 transition-colors">
                  — Justin Moran, CEO, SupperSquad
                </p>
              </Card>
              <Card className="min-w-[400px] md:min-w-[500px]">
                <p className="text-2xl md:text-3xl font-bold uppercase tracking-tighter mb-6 group-hover:text-accent-foreground transition-colors">
                  "THEY DON'T JUST CREATE CAMPAIGNS- THEY CREATE MOVEMENTS."
                </p>
                <p className="text-lg text-muted-foreground group-hover:text-accent-foreground/80 transition-colors">
                  — Arjun Patel, COO, AxA Trading
                </p>
              </Card>
              <Card className="min-w-[400px] md:min-w-[500px]">
                <p className="text-2xl md:text-3xl font-bold uppercase tracking-tighter mb-6 group-hover:text-accent-foreground transition-colors">
                  "RESULTS SPEAK LOUDER THAN WORDS. WIND DELIVERS BOTH."
                </p>
                <p className="text-lg text-muted-foreground group-hover:text-accent-foreground/80 transition-colors">
                  — Nathaniel Anderson, Founder, Albumified
                </p>
              </Card>
            </div>
          </MotionMarquee>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 max-w-[95vw] mx-auto px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter mb-12 leading-none">
            LET'S TALK
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground mb-12 leading-tight">
            Ready to make some noise? Drop us a line and let's build something that moves.
          </p>

          {formStatus === 'success' ? (
            <div className="border-2 border-accent bg-background p-10 text-center space-y-4">
              <div className="text-5xl">✓</div>
              <h3 className="text-3xl font-black uppercase tracking-tighter text-foreground" style={{ fontFamily: "'Poppins Black', sans-serif" }}>
                Message Sent
              </h3>
              <p className="text-muted-foreground text-lg">We'll be in touch soon.</p>
              <button
                onClick={() => setFormStatus('idle')}
                className="mt-4 text-xs uppercase tracking-widest font-black text-accent hover:text-foreground transition-colors focus:outline-none"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form className="space-y-8" onSubmit={handleContactSubmit}>
              <div>
                <Input
                  placeholder="YOUR NAME"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="YOUR EMAIL"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <Input
                  as="textarea"
                  placeholder="YOUR MESSAGE"
                  value={formMessage}
                  onChange={(e) => setFormMessage(e.target.value)}
                  required
                />
              </div>
              {formStatus === 'error' && (
                <p className="text-sm uppercase tracking-widest font-bold text-red-400">{formError}</p>
              )}
              <Button
                size="large"
                type="submit"
                className="w-full"
                disabled={formStatus === 'sending'}
              >
                {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          )}
        </div>
      </section>
      </>
      )}

      {/* Footer */}
      <footer className="py-20 border-t-2 border-border bg-muted">
        <div className="max-w-[95vw] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <h3 
                onClick={() => navigate('/')} 
                className="text-3xl font-bold uppercase tracking-tighter mb-4 flex items-start cursor-pointer select-none hover:opacity-85 transition-opacity" 
                style={{ fontFamily: "'Poppins Black', sans-serif" }}
              >
                <img src={windLogoWhite} alt="Wind Logo" className="w-11 h-9 mr-2 flex-shrink-0" />
                <span style={{ marginTop: '4px', marginLeft: '-7px' }}>WIND</span>
              </h3>
              <p className="text-muted-foreground">
                Marketing that moves.
              </p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wide mb-4">Services</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="/web-design" onClick={(e) => { e.preventDefault(); navigate('/web-design'); }} className="hover:text-accent transition-colors">Small Business Web Design</a></li>
                <li><a href="/web-design-chicago" onClick={(e) => { e.preventDefault(); navigate('/web-design-chicago'); }} className="hover:text-accent transition-colors">Chicago Website Design</a></li>
                <li><a href="/social-media" onClick={(e) => { e.preventDefault(); navigate('/social-media'); }} className="hover:text-accent transition-colors">Social Media Marketing</a></li>
                <li><a href="/logos" onClick={(e) => { e.preventDefault(); navigate('/logos'); }} className="hover:text-accent transition-colors">Logo &amp; Brand Identity</a></li>
                <li><a href="/automation" onClick={(e) => { e.preventDefault(); navigate('/automation'); }} className="hover:text-accent transition-colors">Marketing Automation</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wide mb-4">Company</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="/about" onClick={(e) => { e.preventDefault(); navigate('/about'); }} className="hover:text-accent transition-colors">About</a></li>
                <li><a href="/work" onClick={(e) => { e.preventDefault(); navigate('/work'); }} className="hover:text-accent transition-colors">Work</a></li>
                <li><a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }} className="hover:text-accent transition-colors">Pricing</a></li>
                <li><a href="/clients" onClick={(e) => { e.preventDefault(); navigate('/clients'); }} className="hover:text-accent transition-colors">Clients</a></li>
                <li><a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }} className="hover:text-accent transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wide mb-4">Connect</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="https://www.linkedin.com/company/windmarketing" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">LinkedIn</a></li>
                <li><a href="https://www.youtube.com/@justinmoran" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">YouTube</a></li>
                <li><a href="https://www.instagram.com/i.am.justinmoran" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">Instagram</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t-2 border-border pt-8 text-center text-muted-foreground">
            <p className="text-sm uppercase tracking-wide">
              © {new Date().getFullYear()} WIND MARKETING. ALL RIGHTS RESERVED.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
